import { useEffect, useMemo } from 'react';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { SectionBox, SignalBadge, Table } from '@/components';
import { CategoryStatRow } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  complianceCategoryStatsSelectors,
  showDemoDataSelectors,
} from '@/redux/selectors';
import { complianceCategoryStatsActions } from '@/redux/actions';
import { formatPercent, formatUSD } from '@/helpers';
import { entityDemoData } from './Entity.constants';

const Entity = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: categoryStats, loading } = useAppSelector(
    complianceCategoryStatsSelectors.getComplianceCategoryStatsSlice,
  );

  const isShowDemoData = useAppSelector(showDemoDataSelectors.byCompliance);

  useEffect(() => {
    if (!isShowDemoData) {
      dispatch(complianceCategoryStatsActions.fetchCategoryStats());
    }
  }, [isShowDemoData]);

  const data = useMemo(() => {
    return isShowDemoData ? entityDemoData : categoryStats;
  }, [isShowDemoData, categoryStats]);

  const columns: TableColumnsType<CategoryStatRow> = [
    {
      title: t('translation.entity_category'),
      dataIndex: 'entityCategory',
      render: (value) => <SignalBadge value={value} />,
    },
    {
      title: t('translation.total_funds'),
      dataIndex: 'totalFunds',
      sorter: {
        compare: (a, b) => a.totalFunds - b.totalFunds,
      },
      render: (value) => formatUSD(value),
    },
    {
      title: t('translation.received_funds'),
      dataIndex: 'receivedFunds',
      sorter: {
        compare: (a, b) => a.receivedFunds - b.receivedFunds,
      },
      render: (value) => formatUSD(value),
    },
    {
      title: t('translation.sent_funds'),
      dataIndex: 'sentFunds',
      sorter: {
        compare: (a, b) => a.sentFunds - b.sentFunds,
      },
      render: (value) => formatUSD(value),
    },
    {
      title: t('translation.total_expenses'),
      dataIndex: 'totalExpenses',
      sorter: {
        compare: (a, b) => a.totalExpenses - b.totalExpenses,
      },
      render: (value) => formatPercent(value),
    },
    {
      title: t('translation.received_expenses'),
      dataIndex: 'receivedExpenses',
      sorter: {
        compare: (a, b) => a.receivedExpenses - b.receivedExpenses,
      },
      render: (value) => formatPercent(value),
    },
    {
      title: t('translation.sent_expenses'),
      dataIndex: 'sentExpenses',
      sorter: {
        compare: (a, b) => a.sentExpenses - b.sentExpenses,
      },
      render: (value) => formatPercent(value),
    },
  ];

  return (
    <SectionBox>
      {{
        title: t('translation.by_entity_categories'),
        body: (
          <Table
            columns={columns}
            dataSource={data || []}
            scroll={{ x: 'max-content' }}
          />
        ),
      }}
    </SectionBox>
  );
};

export default Entity;
